import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPages.css';

const PrivacyPartners = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <Link to="/" className="back-home">← Back to Home</Link>

        <h1 className="legal-title">Dangol Business Privacy Policy</h1>
        <p className="legal-last-updated">Last updated: September 30, 2026</p>

        <div className="legal-content">
          <p>
            Dangol LLC ("we," "our," or "us") is committed to protecting your privacy. This Business Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our business mobile application, web dashboard, kiosk software, and related services (collectively, the "Service").
          </p>

          <h2>1. Scope</h2>
          <p>
            This privacy policy applies specifically to business users ("you," "Business," or "Merchant") who access the Dangol business platform. If you are an individual customer using the Dangol customer app, please refer to our separate Customer Privacy Policy.
          </p>

          <h2>2. Information We Collect</h2>
          <p>We collect the following types of information from Business users:</p>

          <p>
            <strong>Business Account Information:</strong> When you create a business account, we collect information such as your business name, contact details, authorized user information, and business address.
          </p>

          <p>
            <strong>User-Generated Content:</strong> We collect any content you create within the Service, including loyalty program settings, promotional notifications, branding materials, and business descriptions.
          </p>

          <p>
            <strong>Usage and Analytics Data:</strong> We collect information about how you use the business platform, including dashboard access, feature usage, and system performance data.
          </p>

          <p>
            <strong>Customer Interaction Data:</strong> We collect data about customer interactions with your business through the platform, including scan data, reward redemptions, and customer engagement metrics specific to your shop.
          </p>

          <p>
            <strong>Event & Transaction Data:</strong> When you create or host Events, we collect Event details and related metrics, such as RSVPs and attendance. For Paid Events, payments are processed by our third-party payment processor (Stripe); we do not store your customers' full payment card details, but we may retain limited transaction records (such as amounts and status) related to your Events.
          </p>

          <p>
            <strong>Security and Authentication Data:</strong> We collect information used to secure your account and kiosk, such as the IP addresses of devices accessing the Service and a secure, one-way hash of your 4-digit Kiosk PIN. We never store or have access to your viewable 4-digit PIN.
          </p>

          <h2>3. How We Use Your Information</h2>
          <p>We use your business information to:</p>
          <ul>
            <li>Provide and maintain your business dashboard and loyalty program management tools.</li>
            <li>Process customer scans and reward redemptions at your business location.</li>
            <li>Generate analytics and insights about customer activity specific to your shop.</li>
            <li>Enable you to create and host Events, including Paid Events.</li>
            <li>Administer raffles and promotions you offer, including selecting winners and enabling prize delivery.</li>
            <li>Enable you to send promotional notifications to your customers.</li>
            <li>Provide customer support and technical assistance.</li>
            <li>Improve our business platform and develop new features.</li>
            <li>Prevent fraud and ensure platform security, including using IP addresses and login attempt data to implement rate-limiting and lock out suspicious activity.</li>
            <li>Comply with legal and regulatory requirements.</li>
          </ul>

          <h2>4. How We Share Your Information</h2>
          <p>We do not sell your business information. We may share your information in the following limited circumstances:</p>

          <p>
            <strong>Customer Data Access:</strong> You have access to customer interaction data specific to your business only. This includes scan history, reward progress, and redemption data for customers who have engaged with your shop through the platform.
          </p>

          <p>
            <strong>Event & Promotion Data:</strong> For your Events, you receive aggregate, non-identifying information and trends about attendance and engagement. We do not provide you with attendees' personal contact information, except that when a customer wins a raffle or promotion you offer, we provide the winner's name and email address so you can deliver the prize. You may use any such information only for that purpose and must handle it in compliance with applicable law.
          </p>

          <p>
            <strong>Service Providers:</strong> We share information with trusted third-party service providers who assist us in operating the platform, such as cloud hosting services (Supabase), notification services (Firebase), payment processing for Paid Events (Stripe), and analytics providers. These providers are contractually obligated to protect your information.
          </p>

          <p>
            <strong>Aggregated Data:</strong> We may use business data in aggregated and anonymized form for platform improvement, market research, and statistical analysis. This aggregated data cannot be used to identify individual businesses.
          </p>

          <p>
            <strong>Legal Requirements:</strong> We may disclose information when required by law, court order, or government request, or to protect our legal rights and those of our users.
          </p>

          <h2>5. Data Security</h2>
          <p>
            We implement industry-standard security measures to protect your business information, including encryption, secure data transmission, securely hashing all Kiosk PINs, access controls, and regular security audits. However, no method of electronic transmission or storage is completely secure, and we cannot guarantee absolute security.
          </p>

          <h2>6. Data Retention</h2>
          <p>
            We retain your business information for as long as your business account remains active or as needed to provide the Service. Following account closure, we will retain certain information as required by law, to resolve disputes, enforce our agreements, and for legitimate business purposes. Customer interaction data specific to your business will be retained according to applicable data protection laws and business requirements. For example, transactional logs (such as failed login attempts) may be deleted after a shorter period (e.g., 90 days) as they are no longer needed, while core account information is retained for the life of your account.
          </p>

          <h2>7. Your Business Privacy Rights</h2>
          <p>As a business user, you have control over your information. Depending on your location, you may have the right to:</p>
          <ul>
            <li>Access and review the business information we hold about you.</li>
            <li>Correct any inaccurate business information.</li>
            <li>Request deletion of your business information, subject to legal and business requirements.</li>
            <li>Request a copy of your business data.</li>
          </ul>

          <p>
            <strong>Customer Data Rights:</strong> You are responsible for ensuring that your use of customer data through our platform complies with applicable privacy laws. Customers have the right to control their notification preferences and can opt out of communications from your business through the customer app.
          </p>

          <h2>8. Business Compliance</h2>
          <p>
            As a business user, you are responsible for ensuring that your use of our platform and any customer data you access complies with applicable laws, including data protection regulations (such as GDPR, CCPA), advertising standards, and business licensing requirements in your jurisdiction.
          </p>

          <h2>9. Changes to This Policy</h2>
          <p>
            We may update this Business Privacy Policy from time to time. We will provide the latest version of this Business Privacy Policy in the Service. Your continued use of the Service after such changes constitutes acceptance of the updated policy.
          </p>

          <h2>10. Contact Us</h2>
          <p>
            If you have any questions about this Business Privacy Policy or our privacy practices, please contact us at <a href="mailto:partnersupport@dangolapp.com">partnersupport@dangolapp.com</a>.
          </p>
        </div>

        <div className="legal-footer">
          <Link to="/terms-partners">Terms of Service for Partners</Link>
          <span> | </span>
          <Link to="/privacy">Privacy Policy for Customers</Link>
          <span> | </span>
          <Link to="/">Home</Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPartners;
